import { useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarClock, ClipboardCheck, FileCheck2, FileDown, Gauge, RotateCcw, Scissors, Target, TrendingUp, Users, X } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { InfoTip } from "../components/Erklaerung";
import { Ampel, Badge, Card } from "../components/ui";
import { LineChart, type Point } from "../components/Chart";
import { useApp } from "../../store/AppStore";
import { cutoverStore, useAssignedLandscapes, useCutoverOverrides } from "../../store/cutoverPlan";
import { GATE_STATUS_LABEL, GATE_STATUS_TONE, GATE_STATUS_VALUES, gateStatusStore, useGateStatus, useGateStatusMap, type GateStatus } from "../../store/gateStatus";
import { landscapeRisk } from "../../domain/aggregate";
import { gateChain, gateCurve } from "../../domain/analysis";
import { gateBefund, type GateBefund } from "../../domain/gateBefund";
import type { Dataset } from "../../data/generator";
import { LizenzBlock } from "../components/Lizenz";
import { isFueRelevantLandscape } from "../../data/licensing";
import { contractDeltaLabel, exportGatePdf, gateStatusLabel } from "../../lib/gatePdf";
import type { GateForecast } from "../../domain/analysis";
import type { Ampel as AmpelType, Landscape } from "../../domain/types";
import { daysUntil, formatDateDe, formatFue, formatNum, formatPct, formatTage, todayIso, type RatioTable } from "../../domain/rules";

const AMPEL_LABEL: Record<AmpelType, string> = {
  gruen: "unkritisch", gelb: "beobachten", rot: "kritisch", grau: "nicht bewertet",
};

// Zeitstrahl-Fenster: 01.01.2026 – 31.12.2028.
const T0 = Date.UTC(2026, 0, 1);
const T1 = Date.UTC(2028, 11, 31);
const SPAN = T1 - T0;
const YEARS = [2026, 2027, 2028];

function pct(iso: string): number {
  const t = new Date(iso + "T00:00:00Z").getTime();
  return Math.max(0, Math.min(100, ((t - T0) / SPAN) * 100));
}

// Marker-Geometrie (px). Der Zeitstrahl ist mindestens TL_MIN_W breit; ein Label ist LABEL_W breit —
// zwei Gates kollidieren also, wenn sie näher als LABEL_W / TL_MIN_W (in % der Achse) beieinanderliegen.
const TL_MIN_W = 960;
const LABEL_W = 92;
const LABEL_H = 36;
const LANE = LABEL_H + 6;
const COLLIDE_PCT = (LABEL_W / TL_MIN_W) * 100 + 0.5;

/**
 * Lane je Gate: 0 = oben, 1 = unten, 2 = oben (höher), 3 = unten (tiefer) … Ein Gate nimmt die erste
 * Lane, in der das zuletzt gesetzte Gate weit genug entfernt ist. So verdeckt kein Marker einen anderen.
 */
function assignLanes(positions: number[]): number[] {
  const last: number[] = [];
  return positions.map((x) => {
    let lane = last.findIndex((lx) => x - lx >= COLLIDE_PCT);
    if (lane === -1) { lane = last.length; last.push(x); } else last[lane] = x;
    return lane;
  });
}

export function Gates() {
  const { ds, ratio, contracted } = useApp();
  const [params, setParams] = useSearchParams();
  const risk = landscapeRisk(ds.persons);
  const ls = useAssignedLandscapes();
  const overrides = useCutoverOverrides();
  const statusMap = useGateStatusMap();
  const changed = Object.keys(overrides).length + Object.keys(statusMap).length;
  const today = todayIso();

  // Alle Gates der Kette (auch Engine-/Sondermetrik mit +0), nach Stichtag sortiert.
  const chain = gateChain(ds, ratio, ls);
  const byId = new Map(ls.map((l) => [l.id, l]));
  const gates = chain.map((g) => ({ g, l: byId.get(g.landscapeId)!, d: daysUntil(g.cutover) }));

  // Zeitstrahl: Lanes gegen Überlappung.
  const lanes = assignLanes(gates.map((x) => pct(x.g.cutover)));
  const aboveLevels = Math.max(1, ...lanes.filter((l) => l % 2 === 0).map((l) => l / 2 + 1));
  const belowLevels = Math.max(1, ...lanes.filter((l) => l % 2 === 1).map((l) => (l - 1) / 2 + 1));
  const AXIS = aboveLevels * LANE + 10;
  const BELOW = belowLevels * LANE + 10;
  const TL_H = AXIS + BELOW + 22;
  const todayPct = pct(today);
  const todayInWindow = new Date(today + "T00:00:00Z").getTime() >= T0 && new Date(today + "T00:00:00Z").getTime() <= T1;

  // Bedarfskurve: beginnt heute bei 0, springt erst mit dem ersten FUE-Cutover (A2).
  const curve = gateCurve(ds, ratio, ls, today);
  const curveData: (Point & { date: string; fueAdded: number; kind: string })[] = curve.map((c) => ({
    label: c.kind === "heute" ? "heute" : (byId.get(c.landscapeId!)?.name ?? c.landscapeId!),
    date: c.date, fueAdded: c.fueAdded, kind: c.kind === "heute" ? "heute" : c.fueRelevant ? "fue" : "engine",
    Bedarf: c.cumulative, Vertrag: contracted,
  }));
  const maxCum = Math.max(contracted, ...curve.map((c) => c.cumulative), 0);
  const curveYMax = Math.ceil((maxCum * 1.08) / 1000) * 1000;
  const endCum = curve[curve.length - 1]?.cumulative ?? 0;
  const firstJump = chain.find((g) => g.fueAdded > 0);

  const openId = params.get("ls");
  const openGate = openId ? byId.get(openId) : undefined;

  const openDoc = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("ls", id);
    setParams(next, { replace: true });
  };
  const closeDoc = () => {
    const next = new URLSearchParams(params);
    next.delete("ls");
    setParams(next, { replace: true });
  };

  return (
    <div>
      <PageHeader route="/gates" />

      <Card
        title="Cutover-Zeitstrahl 2026–2028"
        subtitle={<span className="inline-flex items-center gap-1">Cutover je Landschaft · Q4 grau hinterlegt <InfoTip>Farbe = Berechtigungsrisiko (Anteil über-berechtigter Personen), kein Termin-Countdown. Grau: eigene Lizenzmetrik, keine FUE-Bewertung. Q4 (Oktober bis Dezember) ist Peak-Season. Weniger als 60 Tage Vorlauf sind rot markiert. Nahe Termine werden oben/unten gestaffelt, damit kein Marker einen anderen verdeckt. Klick auf ein Gate öffnet das Gate-Dokument.</InfoTip></span>}
        className="mb-6"
      >
        <div className="overflow-x-auto pb-2">
          <div className="relative" style={{ minWidth: TL_MIN_W, height: TL_H }}>
            {/* Q4-Bänder */}
            {YEARS.map((y) => {
              const left = pct(`${y}-10-01`);
              const right = pct(`${y}-12-31`);
              return (
                <div
                  key={`q4-${y}`}
                  className="absolute top-0"
                  style={{
                    left: `${left}%`,
                    width: `${right - left}%`,
                    bottom: 22,
                    backgroundColor: "var(--surface-sunken)",
                    borderRadius: 6,
                  }}
                  title={`Peak-Season Q4 ${y}`}
                />
              );
            })}

            {/* Jahres-Trennlinien + Labels */}
            {YEARS.map((y) => (
              <div key={`yr-${y}`} className="absolute top-0" style={{ left: `${pct(`${y}-01-01`)}%`, bottom: 22 }}>
                <div className="h-full" style={{ borderLeftWidth: 1, borderColor: "var(--border-soft)" }} />
                <div className="text-faint absolute text-xs font-semibold" style={{ left: 4, bottom: -20 }}>
                  {y}
                </div>
              </div>
            ))}

            {/* Achslinie */}
            <div
              className="absolute right-0 left-0"
              style={{ top: AXIS, borderTopWidth: 2, borderColor: "var(--border-strong)" }}
            />

            {/* Heute */}
            {todayInWindow && (
              <div className="absolute top-0" style={{ left: `${todayPct}%`, bottom: 22 }} title={`heute · ${formatDateDe(today)}`}>
                <div className="h-full" style={{ borderLeftWidth: 1, borderLeftStyle: "dashed", borderColor: "var(--color-brand-600)" }} />
                <div className="absolute text-[10px] font-semibold" style={{ left: 3, bottom: -20, color: "var(--color-brand-600)" }}>heute</div>
              </div>
            )}

            {/* Gate-Marker — in Lanes gestaffelt, damit sich nahe Termine nicht verdecken (A1) */}
            {gates.map((x, i) => {
              const left = pct(x.g.cutover);
              const lane = lanes[i];
              const above = lane % 2 === 0;
              const level = Math.floor(lane / 2);
              const ampel: AmpelType = risk[x.l.id]?.ampel ?? "grau";
              const urgent = x.d !== null && x.d >= 0 && x.d < 60;
              const isChanged = x.l.id in overrides;
              const labelTop = above ? AXIS - 10 - LABEL_H - level * LANE : AXIS + 10 + level * LANE;
              const lineTop = above ? labelTop + LABEL_H : AXIS;
              const lineH = above ? AXIS - lineTop : labelTop - AXIS;
              return (
                <div key={x.l.id} className="absolute" style={{ left: `${left}%`, top: 0, height: TL_H }}>
                  {/* Verbindung Label ↔ Achse */}
                  <div className="absolute" style={{ left: 0, top: lineTop, height: lineH, borderLeftWidth: 1, borderColor: "var(--border-strong)" }} />
                  {/* Punkt auf der Achse */}
                  <span
                    className="absolute h-3 w-3 rounded-full"
                    style={{ left: -6, top: AXIS - 5, backgroundColor: x.g.fueRelevant ? "var(--surface-card)" : "var(--border-strong)", borderWidth: 2, borderColor: "var(--border-strong)" }}
                  />
                  <button
                    onClick={() => openDoc(x.l.id)}
                    className="bg-card absolute rounded-md px-1.5 py-1 text-center shadow-card"
                    style={{ left: -LABEL_W / 2, top: labelTop, width: LABEL_W, height: LABEL_H, borderWidth: 1, borderColor: urgent ? "var(--color-amp-red)" : isChanged ? "var(--color-brand-600)" : "var(--border-soft)" }}
                    title={`${x.l.name} · Cutover ${formatDateDe(x.g.cutover)}${isChanged ? ` (geändert, RFP-Plan: ${formatDateDe(cutoverStore.rfpCutover(x.l.id))})` : ""} · ${x.g.fueRelevant ? `+${formatFue(x.g.fueAdded)}` : "+0 FUE (eigene Metrik)"}`}
                  >
                    <div className="text-strong flex items-center justify-center gap-1 truncate text-[11px] font-semibold">
                      <Ampel state={ampel} size={9} />
                      <span className="truncate">{x.l.name}</span>
                    </div>
                    <div className="tnum text-[10px]" style={{ color: urgent ? "var(--color-amp-red)" : "var(--text-faint)" }}>
                      {formatDateDe(x.g.cutover)}{isChanged ? " ✎" : ""}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Bedarfskurve — beginnt heute bei 0, springt erst mit dem ersten FUE-Cutover (A2) */}
      <Card
        title="Bedarfskurve entlang der Cutover-Kette"
        subtitle={<span className="inline-flex items-center gap-1">Rot = kumulierter Bedarf (Stufe je Cutover) · gestrichelt = Vertrag · hohler Punkt = Cutover ohne FUE-Sprung <InfoTip>Heute läuft nur GSP auf RISE — GSP ist ein Engine-System (GTS), also 0 FUE. Der Bedarf einer Landschaft zählt ab ihrem Cutover; jede Person zählt genau einmal, am frühesten Cutover ihrer FUE-Landschaften (R1). Engine-/Sondermetrik-Cutover (GSP, HCM) stehen als Punkt mit +0 auf der Kurve. Der letzte Wert ist der konsolidierte Gesamtbedarf. Wo die rote Linie über den Vertragsdeckel steigt, reicht der Vertrag nicht mehr.</InfoTip></span>}
        className="mb-6"
      >
        <LineChart
          data={curveData}
          yLabel="FUE (kumuliert)"
          yMin={0}
          yMax={curveYMax}
          rotateLabels
          series={[
            { key: "Bedarf", color: "var(--color-amp-red)", step: true },
            { key: "Vertrag", color: "var(--color-brand-500)", dashed: true },
          ]}
          pointHollow={(d, key) => key === "Bedarf" && d.kind !== "fue"}
          pointTitle={(d, key, v) =>
            key === "Vertrag"
              ? `Vertrag: ${formatFue(v)}`
              : d.kind === "heute"
                ? `heute (${formatDateDe(String(d.date))}): ${formatFue(v)} auf RISE`
                : `${d.label} · ${formatDateDe(String(d.date))}: ${d.kind === "engine" ? "+0 FUE (eigene Metrik)" : `+${formatFue(Number(d.fueAdded))}`} → kumuliert ${formatFue(v)}`}
        />
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <MiniKpi label="Start heute" value={formatFue(curve[0]?.cumulative ?? 0)} sub={formatDateDe(today)} />
          <MiniKpi label="Erster Sprung" value={firstJump ? `+${formatFue(firstJump.fueAdded)}` : "—"} sub={firstJump ? `${byId.get(firstJump.landscapeId)?.name} · ${formatDateDe(firstJump.cutover)}` : "kein FUE-Cutover"} />
          <MiniKpi label="Ende der Kette" value={formatFue(endCum)} sub={contractDeltaLabel(endCum, contracted)} tone={endCum > contracted ? "red" : "green"} />
          <MiniKpi label="Vertrag" value={formatFue(contracted)} sub="Vertragsgrundlage · flach über die Laufzeit" />
        </div>
      </Card>

      {/* Gate-Liste mit editierbarem Cutover-Plan (A5) */}
      <Card
        title="Gates im Überblick"
        subtitle={<span className="inline-flex items-center gap-1">nach Stichtag sortiert · Datum und Status je Landschaft setzbar <InfoTip>Ein geändertes Datum verschiebt nur, wann der Bedarf dieses Systems zählt — der Gesamtbedarf am Ende bleibt gleich. Wer schon über ein früheres System zählt, kommt beim späteren Cutover nicht noch einmal dazu; ändert sich die Reihenfolge, wandert der Anker mit. Der Status wird von Menschen gesetzt (mit Datum und Name), nicht gerechnet. Änderungen bleiben im Browser gespeichert; src/data/landscapes.ts bleibt der RFP-Plan. „Auf RFP-Plan zurücksetzen" löscht Datumsänderungen und gesetzte Status.</InfoTip></span>}
        right={
          <button
            type="button"
            onClick={() => cutoverStore.reset()}
            disabled={changed === 0}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-40"
            style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-dim)" }}
          >
            <RotateCcw size={14} /> Auf RFP-Plan zurücksetzen{changed > 0 ? ` (${changed})` : ""}
          </button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse text-sm">
            <thead>
              <tr className="text-faint text-left text-xs uppercase tracking-wide">
                <th className="px-3 py-2 font-medium">Landschaft</th>
                <th className="px-3 py-2 font-medium">Stichtag</th>
                <th className="px-3 py-2 font-medium">Countdown</th>
                <th className="px-3 py-2 text-right font-medium">Bedarf am Gate</th>
                <th className="px-3 py-2 text-right font-medium">kumuliert danach</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Berechtigungsrisiko</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {gates.map((x) => {
                const ampel: AmpelType = risk[x.l.id]?.ampel ?? "grau";
                const urgent = x.d !== null && x.d >= 0 && x.d < 60;
                const isChanged = x.l.id in overrides;
                const rfp = cutoverStore.rfpCutover(x.l.id);
                return (
                  <tr key={x.l.id} style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                    <td className="px-3 py-3">
                      <div className="text-strong font-semibold">{x.l.name}</div>
                      <div className="text-faint text-xs">{x.l.group} · {x.l.unit}</div>
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="date"
                        aria-label={`Cutover ${x.l.name}`}
                        value={x.g.cutover}
                        min="2026-01-01"
                        max="2028-12-31"
                        onChange={(e) => { if (e.target.value) cutoverStore.set(x.l.id, e.target.value); }}
                        className="tnum bg-card rounded-md px-2 py-1 text-sm"
                        style={{ borderWidth: 1, borderColor: isChanged ? "var(--color-brand-600)" : "var(--border-soft)", color: "var(--text-strong)" }}
                      />
                      {isChanged && (
                        <div className="mt-1 flex items-center gap-1 text-[11px]" style={{ color: "var(--color-brand-600)" }}>
                          geändert, RFP-Plan: <span className="tnum">{formatDateDe(rfp)}</span>
                          <button type="button" onClick={() => cutoverStore.clear(x.l.id)} className="underline" title="RFP-Datum wiederherstellen">zurück</button>
                        </div>
                      )}
                    </td>
                    <td className="tnum px-3 py-3" style={{ color: urgent ? "var(--color-amp-red)" : "var(--text-normal)" }}>
                      {x.d !== null && x.d >= 0 ? formatTage(x.d) : "—"}
                      {urgent && <span className="ml-1 text-[11px] font-semibold">&lt; 60</span>}
                    </td>
                    <td className="tnum px-3 py-3 text-right">
                      {x.g.fueRelevant ? (
                        <>
                          <div className="text-strong font-semibold">+{formatFue(x.g.fueAdded)}</div>
                          <div className="text-faint text-xs">{formatNum(x.g.persons)} Personen · {formatFue(x.g.avoidable)} vermeidbar</div>
                        </>
                      ) : (
                        <>
                          <div className="text-faint font-semibold">+0 FUE</div>
                          <div className="text-faint text-xs">eigene Metrik</div>
                        </>
                      )}
                    </td>
                    <td className="tnum px-3 py-3 text-right">
                      <div className="text-strong font-semibold">{formatFue(x.g.cumulative)}</div>
                      <div className="text-xs" style={{ color: x.g.cumulative > contracted ? "var(--color-amp-red)" : "var(--text-faint)" }}>{contractDeltaLabel(x.g.cumulative, contracted)}</div>
                    </td>
                    <td className="px-3 py-3">
                      <StatusEditor id={x.l.id} compact />
                    </td>
                    <td className="px-3 py-3">
                      {/* Ein farbiger Punkt allein ist nicht lesbar — grau war von
                          „noch nicht geladen" nicht zu unterscheiden. */}
                      <span className="inline-flex items-center gap-2">
                        <Ampel state={ampel} />
                        <span className="text-dim text-xs">
                          {ampel === "grau"
                            ? "nicht bewertet"
                            : `${AMPEL_LABEL[ampel]} · ${formatPct((risk[x.l.id]?.share ?? 0) * 100, 0)}`}
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={() => openDoc(x.l.id)}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium"
                        style={{ backgroundColor: "var(--color-brand-600)", color: "#fff" }}
                      >
                        <FileCheck2 size={14} /> Gate-Dokument
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Gate-Dokument (Modal) */}
      {openGate && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4" style={{ backgroundColor: "rgba(0,0,0,0.45)" }} onClick={closeDoc}>
          <div
            className="bg-card my-8 w-full max-w-2xl rounded-xl shadow-card"
            style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <GateDocument
              l={openGate}
              ds={ds}
              ratio={ratio}
              landscapes={ls}
              contracted={contracted}
              risk={risk}
              gf={chain.find((g) => g.landscapeId === openGate.id)}
              onClose={closeDoc}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function MiniKpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "red" | "green" }) {
  return (
    <div className="rounded-lg px-3 py-2" style={{ borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}>
      <div className="text-faint text-[11px] font-medium uppercase tracking-wide">{label}</div>
      <div className="tnum text-lg font-semibold" style={{ color: tone === "red" ? "var(--color-amp-red)" : tone === "green" ? "var(--color-amp-green)" : "var(--text-strong)" }}>{value}</div>
      {sub && <div className="text-faint tnum text-xs">{sub}</div>}
    </div>
  );
}

function Tile({ icon, label, value, sub, color }: { icon: ReactNode; label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="rounded-lg px-4 py-3" style={{ borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}>
      <div className="text-faint flex items-center gap-1 text-xs font-medium uppercase tracking-wide">{icon} {label}</div>
      <div className="tnum mt-1 text-2xl font-semibold" style={{ color: color ?? "var(--text-strong)" }}>{value}</div>
      {sub && <div className="text-faint tnum text-xs">{sub}</div>}
    </div>
  );
}

/**
 * Gate-Status setzen: Select + Datum + Name. `compact` = Listenzeile (Datum/Name nur, wenn ein
 * Status ≠ offen gesetzt ist); sonst der volle Block im Gate-Dokument.
 */
function StatusEditor({ id, compact }: { id: string; compact?: boolean }) {
  const st = useGateStatus(id);
  const isSet = gateStatusStore.isSet(id);
  const update = (patch: { status?: GateStatus; datum?: string; von?: string }) => {
    const next = { ...st, ...patch };
    // Beim Wechsel weg von „offen" das heutige Datum vorbelegen — überschreibbar.
    if (patch.status && patch.status !== "offen" && !next.datum) next.datum = todayIso();
    if (next.status === "offen") { delete next.datum; delete next.von; }
    gateStatusStore.set(id, next);
  };
  const inputStyle = { borderWidth: 1, borderColor: isSet ? "var(--color-brand-600)" : "var(--border-soft)", color: "var(--text-strong)" } as const;
  const showDetails = !compact || st.status !== "offen";
  return (
    <div className={compact ? "space-y-1" : "space-y-2"}>
      <div className="flex items-center gap-2">
        <Badge tone={GATE_STATUS_TONE[st.status]}>{GATE_STATUS_LABEL[st.status]}</Badge>
        <select
          aria-label={`Status ${id}`}
          value={st.status}
          onChange={(e) => update({ status: e.target.value as GateStatus })}
          className="bg-card rounded-md px-2 py-1 text-xs"
          style={inputStyle}
        >
          {GATE_STATUS_VALUES.map((v) => <option key={v} value={v}>{GATE_STATUS_LABEL[v]}</option>)}
        </select>
      </div>
      {showDetails && (
        <div className={`flex ${compact ? "flex-col gap-1" : "flex-wrap items-center gap-2"}`}>
          <input
            type="date"
            aria-label={`Statusdatum ${id}`}
            value={st.datum ?? ""}
            onChange={(e) => update({ datum: e.target.value || undefined })}
            className="tnum bg-card rounded-md px-2 py-1 text-xs"
            style={inputStyle}
          />
          <input
            type="text"
            aria-label={`Status gesetzt von ${id}`}
            value={st.von ?? ""}
            placeholder="gesetzt von"
            maxLength={80}
            onChange={(e) => update({ von: e.target.value || undefined })}
            className={`bg-card rounded-md px-2 py-1 text-xs ${compact ? "w-36" : "w-56"}`}
            style={inputStyle}
          />
          {!compact && isSet && (
            <button type="button" onClick={() => gateStatusStore.clear(id)} className="text-faint text-xs underline">zurück auf offen</button>
          )}
        </div>
      )}
    </div>
  );
}

const SCHWERE_COLOR = { rot: "var(--color-amp-red)", gelb: "var(--color-amp-amber)" } as const;

function GateDocument({
  l,
  ds,
  ratio,
  landscapes,
  contracted,
  risk,
  gf,
  onClose,
}: {
  l: Landscape;
  ds: Dataset;
  ratio: RatioTable;
  landscapes: readonly Landscape[];
  contracted: number;
  risk: ReturnType<typeof landscapeRisk>;
  gf?: GateForecast;
  onClose: () => void;
}) {
  const id = l.id;
  const [gtsVolume, setGtsVolume] = useState("");
  const status = useGateStatus(id);
  const ampel: AmpelType = risk[id]?.ampel ?? "grau";
  const r = risk[id];
  const fueRelevant = isFueRelevantLandscape(id);
  const d = daysUntil(l.cutover);
  const urgent = d !== null && d >= 0 && d < 60;
  const rfp = cutoverStore.rfpCutover(id);
  const isChanged = cutoverStore.isOverridden(id);
  const deltaColor = (cum: number) => (cum > contracted ? "var(--color-amp-red)" : "var(--color-amp-green)");
  // Risiken, Konfidenz und Empfehlung aus den Daten — kein Freitext aus einer Tabelle.
  const befund: GateBefund = useMemo(() => gateBefund(id, ds, ratio, landscapes, contracted), [id, ds, ratio, landscapes, contracted]);
  const k = befund.konfidenz;

  const onPdf = () => exportGatePdf({ landscape: l, gate: gf, befund, status, contracted, risk: r, rfpCutover: rfp, daysLeft: d });

  return (
    <div>
      <header className="flex items-start justify-between gap-4 px-6 pt-5 pb-4" style={{ borderBottomWidth: 1, borderColor: "var(--border-soft)" }}>
        <div>
          <div className="text-faint flex items-center gap-2 text-xs font-medium uppercase tracking-wide">
            <FileCheck2 size={14} /> Gate-Dokument
          </div>
          <h2 className="text-strong mt-1 flex items-center gap-2 text-xl font-bold">
            <Ampel state={ampel} /> {l.name}
          </h2>
          <div className="text-dim mt-0.5 text-sm">{l.group} · {l.unit} · Systeme {l.systems.join(", ") || "—"}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onPdf}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium"
            style={{ backgroundColor: "var(--color-brand-600)", color: "#fff" }}
          >
            <FileDown size={14} /> Gate-Dokument als PDF
          </button>
          <button onClick={onClose} className="text-faint hover:text-strong rounded-lg p-1" aria-label="Schließen"><X size={20} /></button>
        </div>
      </header>

      <div className="space-y-5 px-6 py-5">
        {/* AP1: Dieser Cutover findet statt — er bringt nur keinen FUE-Bedarf mit,
            weil das System nach einer eigenen Metrik abgerechnet wird. */}
        {!fueRelevant && (
          <div
            className="rounded-lg px-4 py-3"
            style={{ borderWidth: 1, borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}
          >
            <div className="text-strong inline-flex items-center gap-1 text-sm font-semibold">Kein FUE-Bedarf aus diesem Cutover <InfoTip>Dieses System wird über eine eigene Lizenzmetrik abgerechnet, nicht über Berechtigungsklassen. Es erhöht den FUE-Bedarf nicht — der kumulierte Stand bleibt am Gate gleich. Was stattdessen zu klären ist, steht unten.</InfoTip></div>
            {gf && (
              <div className="text-dim tnum mt-1 text-sm">+0 FUE · kumuliert vor und nach dem Gate: {formatFue(gf.cumulative)}</div>
            )}
          </div>
        )}

        {/* Kernfrage des Gates (§5, A3): Was bringt dieses System, was ist vermeidbar, was bleibt? */}
        {gf && fueRelevant && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Tile icon={<TrendingUp size={13} />} label="Bedarf dieses Systems" value={`+${formatFue(gf.fueAdded)}`} sub="kommt mit dem Cutover dazu" color="var(--color-amp-red)" />
            <Tile icon={<Scissors size={13} />} label="davon vermeidbar" value={formatFue(gf.avoidable)} sub={`gefahrlos ${formatFue(gf.avoidableSafe)}`} color="var(--color-amp-amber)" />
            <Tile icon={<Target size={13} />} label="Zielwert nach Optimierung" value={formatFue(gf.target)} sub={`${formatFue(gf.fueAdded)} − ${formatFue(gf.avoidable)}`} color="var(--color-amp-green)" />
            <Tile icon={<Users size={13} />} label="Personen" value={formatNum(gf.persons)} sub="zählen hier erstmals (R1)" />
          </div>
        )}

        {/* Kumulierter Stand vor/nach dem Gate gegen den Vertrag (A3) */}
        {gf && (
          <div className="overflow-x-auto rounded-lg" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-faint text-left text-xs uppercase tracking-wide" style={{ backgroundColor: "var(--surface-sunken)" }}>
                  <th className="px-3 py-2 font-medium">
                    <span className="inline-flex items-center gap-1">Kumulierter Bedarf am Gate <InfoTip>„Vor dem Gate" = Summe aller früheren FUE-Cutover. „Nach dem Gate" = plus Bedarf dieses Systems. „Mit Optimierung" = plus Zielwert, d. h. wenn die vermeidbaren FUE dieses Systems vor dem Cutover abgebaut werden; frühere Systeme bleiben dabei, wie sie sind. Vertrag = Summe der Vertragsgrundlage je Klasse (Einstellungen) — der Vertrag kennt keine Verteilung auf Landschaften.</InfoTip></span>
                  </th>
                  <th className="px-3 py-2 text-right font-medium">FUE</th>
                  <th className="px-3 py-2 text-right font-medium">Vertrag</th>
                  <th className="px-3 py-2 text-right font-medium">Differenz</th>
                </tr>
              </thead>
              <tbody className="tnum">
                <tr style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                  <td className="text-dim px-3 py-2">vor dem Gate</td>
                  <td className="text-strong px-3 py-2 text-right font-semibold">{formatFue(gf.cumulativeBefore)}</td>
                  <td className="text-dim px-3 py-2 text-right">{formatFue(contracted)}</td>
                  <td className="px-3 py-2 text-right" style={{ color: deltaColor(gf.cumulativeBefore) }}>{contractDeltaLabel(gf.cumulativeBefore, contracted)}</td>
                </tr>
                {fueRelevant ? (
                  <>
                    <tr style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                      <td className="text-dim px-3 py-2">+ Bedarf {l.name}</td>
                      <td className="px-3 py-2 text-right" style={{ color: "var(--color-amp-red)" }}>+{formatFue(gf.fueAdded)}</td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2"></td>
                    </tr>
                    <tr style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                      <td className="text-strong px-3 py-2 font-semibold">= nach dem Gate</td>
                      <td className="text-strong px-3 py-2 text-right font-semibold">{formatFue(gf.cumulative)}</td>
                      <td className="text-dim px-3 py-2 text-right">{formatFue(contracted)}</td>
                      <td className="px-3 py-2 text-right font-semibold" style={{ color: deltaColor(gf.cumulative) }}>{contractDeltaLabel(gf.cumulative, contracted)}</td>
                    </tr>
                    <tr style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                      <td className="text-dim px-3 py-2">− davon vermeidbar <span className="text-faint text-xs">(gefahrlos {formatFue(gf.avoidableSafe)})</span></td>
                      <td className="px-3 py-2 text-right" style={{ color: "var(--color-amp-amber)" }}>−{formatFue(gf.avoidable)}</td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2"></td>
                    </tr>
                    <tr style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                      <td className="text-strong px-3 py-2 font-semibold">= nach dem Gate mit Optimierung</td>
                      <td className="text-strong px-3 py-2 text-right font-semibold">{formatFue(gf.cumulativeOptimized)}</td>
                      <td className="text-dim px-3 py-2 text-right">{formatFue(contracted)}</td>
                      <td className="px-3 py-2 text-right font-semibold" style={{ color: deltaColor(gf.cumulativeOptimized) }}>{contractDeltaLabel(gf.cumulativeOptimized, contracted)}</td>
                    </tr>
                  </>
                ) : (
                  <>
                    <tr style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                      <td className="text-dim px-3 py-2">+ Bedarf {l.name}</td>
                      <td className="text-faint px-3 py-2 text-right">+0 FUE</td>
                      <td className="px-3 py-2"></td>
                      <td className="text-faint px-3 py-2 text-right text-xs">eigene Metrik</td>
                    </tr>
                    <tr style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                      <td className="text-strong px-3 py-2 font-semibold">= nach dem Gate</td>
                      <td className="text-strong px-3 py-2 text-right font-semibold">{formatFue(gf.cumulative)}</td>
                      <td className="text-dim px-3 py-2 text-right">{formatFue(contracted)}</td>
                      <td className="px-3 py-2 text-right font-semibold" style={{ color: deltaColor(gf.cumulative) }}>{contractDeltaLabel(gf.cumulative, contracted)}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div>
            <div className="text-faint flex items-center gap-1 text-xs font-medium uppercase tracking-wide">
              <CalendarClock size={13} /> Stichtag
            </div>
            <div className="text-strong tnum mt-1 text-sm font-semibold">{formatDateDe(l.cutover)}</div>
            <div className="tnum text-xs" style={{ color: urgent ? "var(--color-amp-red)" : "var(--text-faint)" }}>
              {d !== null && d >= 0 ? `noch ${formatTage(d)}` : "Stichtag vergangen"}
            </div>
            {isChanged && (
              <div className="tnum text-xs" style={{ color: "var(--color-brand-600)" }}>geändert, RFP-Plan: {formatDateDe(rfp)}</div>
            )}
          </div>
          <div>
            <div className="text-faint flex items-center gap-1 text-xs font-medium uppercase tracking-wide">
              Konfidenz <InfoTip size={12}>{k.herleitung} — Trace-Abdeckung: Anteil Konten in {l.systems.join(", ") || "—"} mit Workload-Statistik im Trace-Zeitraum. Schlüsselabdeckung: Anteil Konten mit Personalnummer (Konsolidierungsschlüssel). Beide gleich gewichtet.</InfoTip>
            </div>
            <div className="text-strong tnum mt-1 text-sm font-semibold">{k.konten > 0 ? formatPct(k.prozent, 0) : "—"}</div>
            <div className="text-faint tnum text-xs">Trace {formatPct(k.traceAbdeckung * 100, 0)} · Schlüssel {formatPct(k.schluesselAbdeckung * 100, 0)} · {formatNum(k.konten)} Konten</div>
          </div>
          <div>
            <div className="text-faint text-xs font-medium uppercase tracking-wide">Größte Maßnahme</div>
            {befund.groessteMassnahme ? (
              <>
                <div className="text-strong mt-1 truncate text-sm font-semibold" title={befund.groessteMassnahme.roleName}>{befund.groessteMassnahme.roleName}</div>
                <div className="text-faint tnum text-xs">{befund.groessteMassnahme.sid} · {befund.groessteMassnahme.authObject} · −{formatFue(befund.groessteMassnahme.deltaFue)}</div>
              </>
            ) : (
              <div className="text-faint mt-1 text-sm">—</div>
            )}
          </div>
        </div>

        {/* Bedarf / Über-Berechtigung */}
        {r && (
          <div className="rounded-lg px-4 py-3" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
            <div className="text-strong inline-flex items-center gap-1 text-sm font-semibold">Berechtigungsbild <InfoTip size={12}>Personen mit Bezug zu dieser Landschaft (Σ über Landschaften &gt; Gesamt, R1) und ihr Anteil über-berechtigter Personen. Das ist die Ampelfarbe auf dem Zeitstrahl.</InfoTip></div>
            <div className="text-dim mt-2 grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-strong tnum text-lg font-semibold">{formatNum(r.persons)}</div>
                <div className="text-faint text-xs">Personen mit Bezug</div>
              </div>
              <div>
                <div className="text-strong tnum text-lg font-semibold">{formatNum(r.lifted)}</div>
                <div className="text-faint text-xs">über-berechtigt</div>
              </div>
              <div>
                <div className="text-strong tnum text-lg font-semibold">{formatNum(r.share * 100)} %</div>
                <div className="text-faint text-xs">Anteil</div>
              </div>
            </div>
            {/* AP1: Über-Berechtigung bleibt hier ein Sicherheitsthema — nur eben keines,
                das sich in FUE ausdrückt. Das muss dastehen, sonst widerspricht die Zahl
                der Aussage „nicht bewertet" weiter oben. */}
            {!fueRelevant && (
              <div className="text-faint mt-2 inline-flex items-center gap-1 text-xs">keine Lizenzwirkung <InfoTip>Ohne FUE-Metrik bleibt Über-Berechtigung ein Berechtigungs- und Sicherheitsthema, fließt aber in keine Bedarfs- oder Einsparrechnung ein.</InfoTip></div>
            )}
          </div>
        )}

        {/* Risiken aus den Daten */}
        <div>
          <div className="text-strong inline-flex items-center gap-1 text-sm font-semibold">Risiken aus den Daten <InfoTip>Jedes Risiko ist eine Regel über eine Zahl: Vorlauf &lt; 60 Tage (rot); Anteil über-berechtigter Personen ≥ 30 % rot, ≥ 20 % gelb; Anteil Personen ohne Trace-Aufzeichnung &gt; 10 % gelb; Einstufung als Annahme oder Engine-Zuordnung offen (gelb); kumuliert nach dem Gate &gt; Vertrag (rot); Engine-/HCM-Gate ohne Vertragswert der Metrik (rot). Ein vermeidbarer Anteil &gt; 25 % des Gate-Bedarfs ist ein Hebel, kein Risiko.</InfoTip></div>
          {befund.risiken.length > 0 || befund.hebel ? (
            <div className="mt-2 overflow-x-auto rounded-lg" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {befund.risiken.map((rk) => (
                    <tr key={rk.id} style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                      <td className="px-3 py-2"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: SCHWERE_COLOR[rk.schwere] }} title={rk.schwere === "rot" ? "kritisch" : "beobachten"} /></td>
                      <td className="text-strong px-3 py-2 font-medium">{rk.label}</td>
                      <td className="tnum px-3 py-2 text-right font-semibold" style={{ color: SCHWERE_COLOR[rk.schwere] }}>{rk.wert}</td>
                      <td className="px-3 py-2 text-right"><InfoTip size={13}>{rk.herleitung}</InfoTip></td>
                    </tr>
                  ))}
                  {befund.hebel && (
                    <tr style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                      <td className="px-3 py-2"><Scissors size={12} style={{ color: "var(--color-amp-green)" }} /></td>
                      <td className="text-strong px-3 py-2 font-medium">{befund.hebel.label}</td>
                      <td className="tnum px-3 py-2 text-right font-semibold" style={{ color: "var(--color-amp-green)" }}>{befund.hebel.wert}</td>
                      <td className="px-3 py-2 text-right"><InfoTip size={13}>{befund.hebel.herleitung}</InfoTip></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-faint mt-1 text-sm">Keine Risiken nach Regelwerk.</p>
          )}
        </div>

        {/* Engine-Metrik: GTS-Belegvolumen erfassen (A3.3) — nur wo eine Engine läuft (GSP/GTS) */}
        {id === "L1" && (
          <div className="rounded-lg px-4 py-3" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
            <div className="text-strong flex items-center gap-1.5 text-sm font-semibold">
              <Gauge size={15} /> Engine-Metrik: GTS
            </div>
            <div className="text-dim mt-1 inline-flex items-center gap-1 text-sm">erwartetes Jahresvolumen <InfoTip>GTS bemisst sich nach Belegvolumen, nicht nach Personen, und wird nie in FUE umgerechnet.</InfoTip></div>
            <div className="mt-3 flex items-center gap-2">
              <input
                type="number"
                min={0}
                value={gtsVolume}
                onChange={(e) => setGtsVolume(e.target.value)}
                placeholder="z. B. 250000"
                className="tnum bg-card w-48 rounded-md px-3 py-2 text-right text-sm"
                style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-strong)" }}
              />
              <span className="text-faint text-sm">Belege / Jahr</span>
            </div>
            <div className="text-faint mt-2 text-xs">
              {gtsVolume && Number.isFinite(Number(gtsVolume)) && Number(gtsVolume) > 0
                ? `Erfasst: ${formatNum(Number(gtsVolume))} Belege / Jahr — fließt in das GTS-Assessment ein, nicht in die FUE-Rechnung.`
                : "Noch kein Volumen erfasst — die GTS-Metrik bleibt vertraglich offen."}
            </div>
          </div>
        )}

        {/* AP1: Lizenzmodell der Systeme dieser Landschaft */}
        <LizenzBlock landscapeId={id} />

        {/* Freigabeempfehlung — aus der Schwere der Risiken */}
        <div
          className="rounded-lg px-4 py-3"
          style={{ borderWidth: 1, borderColor: befund.empfehlung.stufe === "keine" ? "var(--color-amp-red)" : befund.empfehlung.stufe === "auflage" ? "var(--color-amp-amber)" : "var(--color-amp-green)", backgroundColor: "var(--surface-sunken)" }}
        >
          <div className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide" style={{ color: "var(--color-brand-600)" }}>Freigabeempfehlung <InfoTip size={12}>Regel: rote Risiken → „Keine Freigabe"; nur gelbe → „Freigabe unter Auflage"; keine → „Freigabe" (kumuliert ≤ Vertrag ist dann erfüllt, sonst wäre die Vertragslücke rot).</InfoTip></div>
          <p className="text-strong mt-1 text-sm font-medium">{befund.empfehlung.text}</p>
        </div>

        {/* Status — von Menschen gesetzt */}
        <div className="rounded-lg px-4 py-3" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
          <div className="text-strong flex items-center gap-1.5 text-sm font-semibold"><ClipboardCheck size={15} /> Gate-Status</div>
          <div className="text-faint mt-0.5 text-xs">Freigabe: {gateStatusLabel(status)}</div>
          <div className="mt-2"><StatusEditor id={id} /></div>
        </div>

        {/* Signaturblock */}
        <div className="pt-2">
          <div className="text-faint text-xs font-medium uppercase tracking-wide">Freigabe / Unterschriften</div>
          <div className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2">
            {["Lizenzverantwortlicher", "Projektleitung"].map((role) => (
              <div key={role}>
                <div style={{ borderTopWidth: 1, borderColor: "var(--border-strong)" }} />
                <div className="text-dim mt-1 text-xs">{role}</div>
                <div className="text-faint text-xs">Datum, Unterschrift</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
